import { ChevronRight } from 'lucide-react';
import Footer from './Footer';
import { SEO } from './SEO';

import { Scene } from './ThreeScene';
import { Canvas } from '@react-three/fiber';
import KrishnaHeroVideo from './KrishnaHeroVideo';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';


import HPricingSection from './HomePricing';
import RotatingText from './RotatingText';
import Ticker from './Ticker';
import RatingComponent from './RatingComponent';
import HomeProofSection from './HomeProofSection';

import LocalizedLink from './LocalizedLink';

// NOTE: For Indian locales we show Krishna video by default, but if
// the user switches to dark mode on the main page, we prefer the 3D globe
// because the video has a white background that looks visually out of place
// against the dark theme. So: Indian locale + light mode => video,
// Indian locale + dark mode => globe.
function MainPage({user, darkMode}:any) {
  const { t } = useTranslation(['main', 'seo']);

  const [isScrolling, setIsScrolling] = useState(false);
  const [learnMoreHover, setLearnMoreHover] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [questionIndex, setQuestionIndex] = useState(0);
  const isIndianLocale = typeof window !== 'undefined' && Boolean((window as any).__isIndianLocale__);
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > 768;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(min-width: 769px)');
    const update = () => setIsDesktop(mql.matches);
    update();
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    }
    mql.addListener(update);
    return () => mql.removeListener(update);
  }, []);

  useEffect(() => {
    let scrollTimeout:any;

    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const sampleQuestions = [
    "Which Ayurvedic herb helps with stress?",
    "What does the Bhagavad Gita say about karma?",
    "Explain the concept of Dharma",
    "How to perform morning Sandhyavandana?",
    "What is my Dosha type?"
  ];

  useEffect(() => {
    const currentQuestion = sampleQuestions[questionIndex];
    let charIndex = 0;
    setTypedText('');
    const typeInterval = setInterval(() => {
      if (charIndex < currentQuestion.length) {
        setTypedText(currentQuestion.substring(0, charIndex + 1));
        charIndex += 1;
      } else {
        clearInterval(typeInterval);
        setTimeout(() => {
          setQuestionIndex((prev) => (prev + 1) % sampleQuestions.length);
        }, 2000);
      }
    }, 45);

    return () => clearInterval(typeInterval);
  }, [questionIndex]);

  return (
    <>
        <SEO 
          title={t('seo:main.title', { defaultValue: 'Sarvalok | An AI Gateway to Sanātana Dharma & Indic Scriptures' })}
          description={t('seo:main.description', { defaultValue: "The essential AI tool for Indology, research, and spiritual study. Sarvalok offers source-grounded analysis of the Vedas, Upanishads, and Sanātana Dharma texts." })}
          url="/"
        />
      <div className="min-h-screen bg-white text-gray-900">
      {/* Hero Section */}
      
      <div className="container mx-auto px-6 ">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center bg-blue-50 text-blue-600 rounded-full px-4 py-2 mb-6">
            <span className="text-sm font-medium">{t('main.new_badge')}</span>
            <span className="mx-2">•</span>
            <span className="text-sm">{t('main.heritage_ai_engine')}</span>
          </div>
          <h1 className="text-3xl md:text-6xl font-extrabold tracking-[-0.01em] mb-6 text-gray-900">
            {isIndianLocale ? (
              <>
                {t('main.india_hero_title_prefix', { defaultValue: 'Your' })}{' '}
                <RotatingText
                  texts={[
                    t('main.india_trusted', { defaultValue: 'Trusted' }),
                    t('main.india_personal', { defaultValue: 'Personal' }),
                    t('main.india_everyday', { defaultValue: 'Everyday' }),
                  ]}
                  rotationInterval={5000}
                />{' '}
                {t('main.india_hero_title_suffix', { defaultValue: 'Dharmic Guide' })}
              </>
            ) : (
              t('main.hero_title_static', { defaultValue: 'Ancient Wisdom, Clearly Explained' })
            )}
          </h1>
          {!isIndianLocale ? (
            <div className="text-lg md:text-2xl font-medium text-gray-800 mb-4">
              <span>{t('main.hero_subtitle_prefix', { defaultValue: 'For serious ' })}</span>
              <RotatingText
                texts={[
                  t('main.hero_subtitle_seeker', { defaultValue: 'seekers' }),
                  t('main.hero_subtitle_student', { defaultValue: 'students' }),
                  t('main.hero_subtitle_scholar', { defaultValue: 'scholars' }),
                  t('main.hero_subtitle_practitioner', { defaultValue: 'practitioners' }),
                ]}
                rotationInterval={3500}
                staggerDuration={0.02}
                staggerFrom="first"
                splitBy="characters"
                mainClassName="mx-1 rotating-word"
              />
              <span>{t('main.hero_subtitle_suffix', { defaultValue: ' of Dharma' })}</span>
            </div>
          ) : null}
          <p className="text-xl text-gray-600 mb-8" style={{ textWrap: 'balance' }}>
            {t('main.hero_description_v2', { defaultValue: "Sarvalok guides modern seekers beyond summaries and simplifications, connecting you to the deeper wisdom of the tradition with clarity and care." })}
          </p>
          <div className="flex flex-col sm:flex-row items-center sm:space-x-4 space-y-3 sm:space-y-0">
            <LocalizedLink
              to={user ? "/chat" : "/sign-up"}
              className="inline-flex h-[52px] w-full items-center justify-center rounded-lg bg-blue-600 px-6 text-[15px] font-medium text-white transition hover:bg-blue-700 sm:w-auto sm:min-w-[152px]"
            >
              {user ? (
                <>
                  {t('main.continue_chat')}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </>
              ) : (
                <>
                  {t('main.free_trial', { defaultValue: 'Free Trial' })}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </>
              )}
            </LocalizedLink>
            {user?<></>:<LocalizedLink
              to="/why-sarvalok"
              onMouseEnter={() => setLearnMoreHover(true)}
              onMouseLeave={() => setLearnMoreHover(false)}
              onFocus={() => setLearnMoreHover(true)}
              onBlur={() => setLearnMoreHover(false)}
              className="relative inline-flex h-[52px] w-full items-center justify-center rounded-lg border border-gray-200 px-6 text-[15px] font-medium text-gray-900 btn-hover-light transition-colors ease-out hover:bg-gray-50 sm:w-auto sm:min-w-[152px]"
            >
              <span className="flex items-center whitespace-nowrap">
                {t('main.learn_more')}
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  width: learnMoreHover ? '26px' : '0px',
                  marginLeft: learnMoreHover ? '8px' : '0px',
                  overflow: 'hidden',
                  transition: 'width 300ms ease-out, margin-left 300ms ease-out',
                }}
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  style={{
                    width: '18px',
                    height: '18px',
                    opacity: learnMoreHover ? 1 : 0,
                    transform: learnMoreHover ? 'translateX(0)' : 'translateX(-6px)',
                    transition: 'opacity 280ms ease-out 40ms, transform 300ms ease-out',
                    flexShrink: 0,
                    pointerEvents: 'none',
                  }}
                >
                  <path d="M3 8h10M9 4l4 4-4 4" />
                </svg>
              </span>
            </LocalizedLink>}
            {user?<></>:<RatingComponent />}
          </div>
          <p className="flex items-center gap-1.5 text-[13px] tracking-[0.08em] text-gray-500 font-medium mt-6">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-gray-400 flex-shrink-0" aria-hidden="true">
              <path d="M12 2L4 6v5c0 5.25 3.4 10.15 8 11.4 4.6-1.25 8-6.15 8-11.4V6l-8-4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="currentColor" fillOpacity="0.08" />
              <path d="M9 12.5l2 2 4-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('main.hero_tagline', { defaultValue: 'High-authority, curated, Shastra-specific intelligence system' })}
          </p>
          </div>
          {isDesktop && (
            <div className="relative bg-white h-[550px]">
              <div className="rounded-2xl h-full relative">
                { (() => {
                    const showVideo = isIndianLocale && !darkMode;
                    // Indian locale users see Krishna video by default, but when dark mode
                    // is active on the main page we prefer the globe due to video’s white background
                    // clashing with dark UI. Non-Indian locales always see the globe.
                    if (showVideo) {
                      return <KrishnaHeroVideo />;
                    }
                    return (
                      <Canvas
                        className="w-full h-full"
                        camera={{ position: [0, 0, 15], fov: 60 }}
                        gl={{
                          antialias: true,
                          alpha: true,
                          preserveDrawingBuffer: true,
                          powerPreference: 'high-performance',
                        }}
                        dpr={[1, 2]}
                        performance={{ min: 0.5 }}
                        style={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          display: 'block'
                        }}
                      >
                        <Scene />
                      </Canvas>
                    );
                  })() }
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Homepage Bridge Text */}
      <div className="bg-white">
        <div className="container mx-auto px-6">
          <p 
            className="text-center max-w-2xl mx-auto" 
            style={{
              fontFamily: "'Inter', 'Segoe UI', -apple-system, sans-serif",
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: 1.5,
              opacity: 0.85,
              margin: '40px auto 32px'
            }}
          >
            {t('main.bridge_text')}
          </p>
        </div>
        <Ticker />
      </div>

      <HomeProofSection />

      <div className="bg-white chat-demo-section">
        <div className="container mx-auto px-6 py-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 chat-demo-title">
                {t('main.chat_demo_title', { defaultValue: 'See Sarvalok in Action' })}
              </h2>
              <p className="text-lg text-gray-600 chat-demo-subtitle">
                {t('main.chat_demo_subtitle', { defaultValue: 'Ask a question and watch the response take shape, grounded in verified ancient texts.' })}
              </p>
            </div>
            <div className="border border-blue-100 rounded-2xl shadow-xl overflow-hidden bg-white chat-demo-card">
              <div className="px-5 py-4 border-b border-blue-900 bg-blue-900 flex items-center justify-between chat-demo-header">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded bg-white/15 flex items-center justify-center chat-demo-header-avatar">
                    <span className="text-white font-semibold text-sm">S</span>
                  </div>
                  <span className="text-white font-medium chat-demo-header-title">Sarvalok AI</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-white/20 chat-demo-header-dot"></div>
                  <div className="w-3 h-3 rounded-full bg-white/20 chat-demo-header-dot"></div>
                  <div className="w-3 h-3 rounded-full bg-white/20 chat-demo-header-dot"></div>
                </div>
              </div>
              <div className="p-5 space-y-5 chat-demo-body">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium bg-blue-50 text-blue-700 chat-demo-user-badge">
                    You
                  </div>
                  <div className="flex-1 rounded-lg p-4 bg-blue-50 text-gray-900 chat-demo-user-bubble">
                    <p className="chat-demo-question-line flex items-center gap-1">
                      <span>{typedText}</span>
                      <span className="inline-block w-2 h-5 bg-blue-700 animate-pulse chat-demo-cursor" />
                    </p>
                  </div>
                </div>
                <div className="h-px bg-blue-100 chat-demo-divider" />
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm bg-blue-600 chat-demo-assistant-badge">
                    S
                  </div>
                  <div className="chat-demo-assistant-content flex-1">
                    <div className="chat-demo-loading-row flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full animate-pulse bg-blue-500 chat-demo-loading-dot"></div>
                      <span className="text-sm text-blue-700/70 chat-demo-loading-text">Searching ancient scriptures...</span>
                    </div>
                    <div className="chat-demo-loading-stack space-y-2">
                      <div className="h-3 rounded w-full bg-blue-100 chat-demo-loading-bar"></div>
                      <div className="h-3 rounded w-4/5 bg-blue-100 chat-demo-loading-bar"></div>
                      <div className="h-3 rounded w-3/5 bg-blue-100 chat-demo-loading-bar"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-blue-100 bg-blue-50 chat-demo-footer">
                <div className="flex items-center gap-2 text-xs text-blue-700/70 chat-demo-footer-text">
                  <svg className="chat-demo-footer-icon w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="chat-demo-footer-note">Sample representation only — power users get advanced filters</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <HPricingSection />

      {/* Footer */}
      <Footer />
    </div>
    </>
  );
}

export default MainPage;
